/**
 * Drizzle query builders + query keys for ASSESSMENT (F3-B).
 *
 * Local-first consumption over the per-role streams: the assignment
 * (content) travels with the course tree; the gradebook (categories/items)
 * and MY submissions/grades via my_cohorts_student; the full roster of
 * submissions/grades via my_cohorts_teacher / org_admin_scope. The
 * gradebook's computed matrix and the final grade are requested from the server (REST) — authoritative.
 */

import {
  and,
  asc,
  assignment,
  assignment_grade,
  assignment_submission,
  cohort,
  cohort_enrollment,
  eq,
  grade_category,
  grade_item,
  isNull,
  type PowerSyncDrizzleDB,
} from "@space/powersync";

export const assessmentQueryKeys = {
  all: ["academy-assessment"] as const,
  courseAssignments: (courseId: string | undefined) =>
    ["academy-assessment", "assignments", courseId] as const,
  assignment: (assignmentId: string | undefined) =>
    ["academy-assessment", "assignment", assignmentId] as const,
  gradeCategories: (cohortId: string | undefined) =>
    ["academy-assessment", "grade-categories", cohortId] as const,
  gradeItems: (cohortId: string | undefined) =>
    ["academy-assessment", "grade-items", cohortId] as const,
  mySubmission: (assignmentId: string | undefined, cohortId: string | undefined) =>
    ["academy-assessment", "my-submission", assignmentId, cohortId] as const,
  myCohortForCourse: (userId: string | undefined, courseId: string | undefined) =>
    ["academy-assessment", "my-cohort-for-course", userId, courseId] as const,
  mySubmissions: (userId: string | undefined) =>
    ["academy-assessment", "my-submissions", userId] as const,
  myGrades: (userId: string | undefined) => ["academy-assessment", "my-grades", userId] as const,
  cohortSubmissions: (cohortId: string | undefined) =>
    ["academy-assessment", "cohort-submissions", cohortId] as const,
  cohortGrades: (cohortId: string | undefined) =>
    ["academy-assessment", "cohort-grades", cohortId] as const,
  gradebook: (cohortId: string | undefined) =>
    ["academy-assessment", "gradebook", cohortId] as const,
};

// ─── Assignments (content) ─────────────────────────────────────────────────────

/** Live assignments of a course. */
export function getCourseAssignmentsQuery(db: PowerSyncDrizzleDB, courseId: string) {
  return db
    .select()
    .from(assignment)
    .where(and(eq(assignment.course_id, courseId), isNull(assignment.deleted_at)))
    .orderBy(asc(assignment.created_at));
}

/** An assignment by id. */
export function getAssignmentQuery(db: PowerSyncDrizzleDB, assignmentId: string) {
  return db.select().from(assignment).where(eq(assignment.id, assignmentId)).limit(1);
}

// ─── Gradebook: categories and items ────────────────────────────────────────────

export function getGradeCategoriesQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(grade_category)
    .where(and(eq(grade_category.cohort_id, cohortId), isNull(grade_category.deleted_at)))
    .orderBy(asc(grade_category.position));
}

export function getGradeItemsQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(grade_item)
    .where(and(eq(grade_item.cohort_id, cohortId), isNull(grade_item.deleted_at)))
    .orderBy(asc(grade_item.position));
}

// ─── Submissions and grades ─────────────────────────────────────────────────────

/** MY submission for an assignment in a cohort. */
export function getMySubmissionQuery(
  db: PowerSyncDrizzleDB,
  assignmentId: string,
  cohortId: string,
  userId: string,
) {
  return db
    .select()
    .from(assignment_submission)
    .where(
      and(
        eq(assignment_submission.assignment_id, assignmentId),
        eq(assignment_submission.cohort_id, cohortId),
        eq(assignment_submission.user_id, userId),
      ),
    )
    .limit(1);
}

/** ALL my submissions (student's page). */
export function getMySubmissionsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(assignment_submission)
    .where(eq(assignment_submission.user_id, userId))
    .orderBy(asc(assignment_submission.submitted_at));
}

/** ALL my grades (student's page / record). */
export function getMyGradesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(assignment_grade)
    .where(eq(assignment_grade.user_id, userId))
    .orderBy(asc(assignment_grade.updated_at));
}

/** All submissions of a cohort (teacher view). */
export function getCohortSubmissionsQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(assignment_submission)
    .where(eq(assignment_submission.cohort_id, cohortId));
}

/** All grades of a cohort (teacher view). */
export function getCohortGradesQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db.select().from(assignment_grade).where(eq(assignment_grade.cohort_id, cohortId));
}

/**
 * MY ACTIVE cohort for a course (to resolve the submission context from the
 * module's player). Returns at most one (local join over
 * my_cohorts_student streams).
 */
export function getMyActiveCohortForCourseQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  courseId: string,
) {
  return db
    .select({
      cohort_id: cohort_enrollment.cohort_id,
      organization_id: cohort_enrollment.organization_id,
    })
    .from(cohort_enrollment)
    .innerJoin(cohort, eq(cohort_enrollment.cohort_id, cohort.id))
    .where(
      and(
        eq(cohort_enrollment.user_id, userId),
        eq(cohort_enrollment.status, "active"),
        isNull(cohort_enrollment.deleted_at),
        eq(cohort.course_id, courseId),
      ),
    )
    .limit(1);
}
